/**
 * Email Settings Page
 *
 * Handles the `office/settings/email/` page with its three provider tabs
 * (Gmail, Outlook, generic IMAP/SMTP):
 *
 *   - Tab switching between the three provider views.
 *   - Spinner on the OAuth "Connect" buttons and on every `.credentials-form`
 *     submit (Gmail client-id form, IMAP/SMTP save form, etc.).
 *   - Live (non-blocking) feedback on the IMAP/SMTP form: as the user
 *     types or changes a select, the previous server-side error styling
 *     on that field is cleared. The same validator drives the async
 *     "Test IMAP" / "Test SMTP" probe buttons (where blocking is the
 *     correct UX — the request never reaches the server otherwise).
 *   - Async "Test IMAP" / "Test SMTP" probe buttons that POST the current
 *     form values to the probe endpoint and render the result beneath
 *     the buttons. The probes scope validation to a field subset so
 *     "Test IMAP" never complains about empty SMTP fields (and vice-versa).
 *
 * Validation strategy: 100% server-side on submit. ``ImapSmtpCredentialsForm``
 * (in ``apps/office/forms_settings.py``) enforces required fields, the
 * 1..65535 port range, the hostname shape and the From/Reply-To e-mail
 * format. Errors come back as ``.field-group.has-error`` blocks and the
 * global ``form-error-scroll`` helper centres the first one after the
 * HTMX/full-page swap. The submit button protection comes from the
 * generic ``data-protect-submit`` machinery in ``utils/forms/submit-button.js``
 * (and the legacy `.credentials-form` listener below, kept until the
 * Phase B button-pattern migration unifies it all). No ``preventDefault``
 * lives on the submit path — see ``frontend/src/pages/team-form.js`` for
 * the canonical pattern.
 *
 * The auto-init hook reads every user-facing string and the test URL
 * template from ``data-*`` attributes on the root container (rendered
 * by the template from Django's ``{% trans %}`` tags), so this module
 * never has to care about the active locale.
 */

var SPINNER_SVG =
    '<svg class="animate-spin -ml-0.5 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">' +
    '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
    '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>' +
    '</svg>';

var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
var HOST_RE = /^[A-Za-z0-9._-]+$/;

// Fields touched by each probe. "Test IMAP" should only complain about
// missing IMAP settings + credentials; "Test SMTP" only about SMTP + credentials.
var IMAP_PROBE_FIELDS = new Set([
    'imap_host', 'imap_port', 'imap_security',
    'imap_smtp_username', 'imap_smtp_password',
]);
var SMTP_PROBE_FIELDS = new Set([
    'smtp_host', 'smtp_port', 'smtp_security',
    'imap_smtp_username', 'imap_smtp_password',
]);

// ========================================================
// TAB SWITCHING
// ========================================================

function initTabSwitching(root) {
    var tabs = [
        { btn: root.querySelector('#tab-gmail-btn'), view: document.getElementById('email-gmail-view') },
        { btn: root.querySelector('#tab-outlook-btn'), view: document.getElementById('email-outlook-view') },
        { btn: root.querySelector('#tab-imapsmtp-btn'), view: document.getElementById('email-imapsmtp-view') },
    ].filter(function (t) { return t.btn && t.view; });
    if (!tabs.length) return;

    var activeClasses = ['bg-white', 'dark:bg-gray-600', 'text-gray-900', 'dark:text-white', 'shadow-sm'];
    var inactiveClasses = ['text-gray-500', 'dark:text-gray-400', 'hover:text-gray-700', 'dark:hover:text-gray-200'];

    function activate(target) {
        tabs.forEach(function (t) {
            var isActive = t === target;
            activeClasses.forEach(function (c) { t.btn.classList.toggle(c, isActive); });
            inactiveClasses.forEach(function (c) { t.btn.classList.toggle(c, !isActive); });
            t.view.classList.toggle('hidden', !isActive);
        });
    }
    tabs.forEach(function (t) {
        t.btn.addEventListener('click', function () { activate(t); });
    });
}

// ========================================================
// OAUTH + CREDENTIAL SUBMIT SPINNERS
// ========================================================

function initOAuthSpinner(root) {
    root.querySelectorAll('.oauth-connect-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            var orig = btn.innerHTML;
            btn.innerHTML = SPINNER_SVG + orig;
            btn.classList.add('opacity-75', 'pointer-events-none');
        });
    });
}

function initCredentialsSpinner(root) {
    root.querySelectorAll('.credentials-form').forEach(function (form) {
        form.addEventListener('submit', function (e) {
            // Skip when client-side validation already cancelled the submit,
            // so the user can fix the flagged fields without a frozen button.
            if (e.defaultPrevented) return;
            var btn = form.querySelector('button[type="submit"]');
            if (btn && !btn.disabled) {
                var orig = btn.innerHTML;
                btn.innerHTML = SPINNER_SVG + orig;
                btn.disabled = true;
                btn.classList.add('opacity-75');
            }
        });
    });
}

// ========================================================
// IMAP/SMTP FORM VALIDATION
// ========================================================

function showError(fieldGroup, input, msg) {
    if (!fieldGroup) return;
    fieldGroup.classList.add('has-error');
    if (input) {
        input.classList.add('border-red-500', 'dark:border-red-500');
        input.classList.remove('border-gray-300', 'dark:border-gray-600');
    }
    fieldGroup.querySelectorAll('.field-error').forEach(function (el) { el.remove(); });
    var p = document.createElement('p');
    p.className = 'mt-1 text-sm text-red-600 dark:text-red-500 field-error';
    p.textContent = msg;
    fieldGroup.appendChild(p);
}

function clearError(input) {
    var fieldGroup = input.closest('.field-group');
    if (!fieldGroup) return;
    fieldGroup.classList.remove('has-error');
    input.classList.remove('border-red-500', 'dark:border-red-500');
    input.classList.add('border-gray-300', 'dark:border-gray-600');
    fieldGroup.querySelectorAll('.field-error').forEach(function (el) { el.remove(); });
}

function validatePort(input) {
    var raw = (input.value || '').trim();
    if (!raw) return false;
    var n = parseInt(raw, 10);
    return !(Number.isNaN(n) || n < 1 || n > 65535);
}

// Returns the offending input when the group is invalid, else null.
// `subset` is an optional Set of field names — when present we only
// validate groups whose input.name is in the set.
function validateFieldGroup(group, messages, options) {
    options = options || {};
    var subset = options.subset;
    var hasExistingPassword = !!options.hasExistingPassword;

    var label = group.querySelector('label');
    var hasAsterisk = !!(label && label.querySelector('.text-red-500'));
    var input = group.querySelector('input:not([type="hidden"]), select, textarea');
    if (!input) return null;

    var name = input.getAttribute('name') || '';
    if (subset && !subset.has(name)) return null;

    var value = (input.value || '').trim();

    // Blank password means "keep the existing one" once a credential is saved.
    if (name === 'imap_smtp_password' && hasExistingPassword) return null;

    if (hasAsterisk && !value) {
        showError(group, input, messages.required);
        return input;
    }
    if (!value) return null;

    if (name === 'imap_port' || name === 'smtp_port') {
        if (!validatePort(input)) {
            showError(group, input, messages.portRange);
            return input;
        }
        return null;
    }
    if (name === 'imap_host' || name === 'smtp_host') {
        if (!HOST_RE.test(value) || value.length > 255) {
            showError(group, input, messages.hostname);
            return input;
        }
        return null;
    }
    if (name === 'mailbox_from_email' || name === 'mailbox_reply_to') {
        if (!EMAIL_RE.test(value)) {
            showError(group, input, messages.email);
            return input;
        }
    }
    return null;
}

function validateImapSmtp(form, messages, options) {
    var firstError = null;
    form.querySelectorAll('.field-group').forEach(function (group) {
        var bad = validateFieldGroup(group, messages, options);
        if (bad && !firstError) firstError = bad;
    });
    return firstError;
}

// ========================================================
// IMAP/SMTP ASYNC TEST BUTTONS
// ========================================================

function initImapSmtpTestButtons(root, form, messages, options) {
    var resultEl = root.querySelector('#imap-smtp-test-result');
    var testUrlTemplate = options.testUrlTemplate || '';
    var csrfInput = form.querySelector('[name=csrfmiddlewaretoken]');
    var csrfToken = csrfInput ? csrfInput.value : '';

    function runTest(probe, button) {
        if (!resultEl) return;

        var subset = probe === 'imap' ? IMAP_PROBE_FIELDS : SMTP_PROBE_FIELDS;
        var firstError = validateImapSmtp(form, messages, {
            hasExistingPassword: options.hasExistingPassword,
            subset: subset,
        });
        if (firstError) {
            var offending = firstError.closest('.field-group') || firstError;
            offending.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstError.focus();
            resultEl.className = 'mt-2 text-sm text-red-700 dark:text-red-400';
            resultEl.textContent = messages.fixHighlighted;
            return;
        }

        var label = button.querySelector('.btn-label');
        var originalLabel = label ? label.textContent : '';
        button.disabled = true;
        button.classList.add('opacity-75');
        if (label) label.textContent = messages.testing;
        resultEl.className = 'mt-2 text-sm text-gray-500 dark:text-gray-400';
        resultEl.textContent = messages.probing;

        var fd = new FormData(form);
        fetch(testUrlTemplate.replace('__PROBE__', probe), {
            method: 'POST',
            headers: { 'X-CSRFToken': csrfToken },
            body: fd,
            credentials: 'same-origin',
        })
            .then(function (r) {
                return r.json().then(function (j) { return { ok: r.ok, json: j }; });
            })
            .then(function (payload) {
                if (payload.ok && payload.json.ok) {
                    resultEl.className = 'mt-2 text-sm text-green-700 dark:text-green-400';
                    resultEl.textContent = probe.toUpperCase() + ' ' + messages.success;
                } else {
                    resultEl.className = 'mt-2 text-sm text-red-700 dark:text-red-400';
                    resultEl.textContent =
                        probe.toUpperCase() + ' ' + messages.failed + ' ' +
                        (payload.json.error || 'Unknown error');
                }
            })
            .catch(function (err) {
                resultEl.className = 'mt-2 text-sm text-red-700 dark:text-red-400';
                resultEl.textContent = messages.networkError + ' ' + err;
            })
            .finally(function () {
                button.disabled = false;
                button.classList.remove('opacity-75');
                if (label) label.textContent = originalLabel;
            });
    }

    root.querySelectorAll('#btn-test-imap, #btn-test-smtp').forEach(function (btn) {
        btn.addEventListener('click', function () {
            runTest(btn.dataset.probe, btn);
        });
    });
}

// ========================================================
// IMAP/SMTP FORM LIFECYCLE
// ========================================================

function initImapSmtpForm(root, messages, options) {
    var form = root.querySelector('#imap-smtp-form');
    if (!form) return;

    // No submit-blocking validator: required/range/shape/email checks
    // are now enforced exclusively by `ImapSmtpCredentialsForm` server-side.
    // `validateImapSmtp` is still used below by the Test IMAP / Test SMTP
    // buttons, which DO need to short-circuit before firing the probe.

    form.addEventListener('input', function (e) {
        var t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') && t.value.trim()) {
            clearError(t);
        }
    });
    form.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.tagName === 'SELECT' && t.value) clearError(t);
        if (t && t.tagName === 'INPUT' && t.value.trim()) clearError(t);
    });

    initImapSmtpTestButtons(root, form, messages, options);
}

// ========================================================
// MAIN INIT
// ========================================================

/**
 * @param {Object}  [config]
 * @param {Element} [config.root]                   Root container (default: document)
 * @param {Object}  [config.messages]               Translated strings
 * @param {string}  [config.messages.required]      "This field is required."
 * @param {string}  [config.messages.portRange]     "Port must be between 1 and 65535."
 * @param {string}  [config.messages.hostname]      "Enter a valid hostname…"
 * @param {string}  [config.messages.email]         "Enter a valid email address."
 * @param {string}  [config.messages.fixHighlighted]"Please fix the highlighted fields…"
 * @param {string}  [config.messages.testing]       "Testing…" (button label while probing)
 * @param {string}  [config.messages.probing]       "Running probe…" (status line)
 * @param {string}  [config.messages.success]       "connection successful."
 * @param {string}  [config.messages.failed]        "failed:"
 * @param {string}  [config.messages.networkError]  "Network error:"
 * @param {string}  [config.testUrlTemplate]        URL with a `__PROBE__` placeholder
 * @param {boolean} [config.hasExistingPassword]    Whether the IMAP/SMTP mailbox already
 *                                                  has a saved password (lets the user
 *                                                  keep it by leaving the field blank).
 */
export function initEmailSettings(config) {
    config = config || {};
    var root = config.root || document;
    var messages = config.messages || {};
    var options = {
        testUrlTemplate: config.testUrlTemplate || '',
        hasExistingPassword: !!config.hasExistingPassword,
    };

    // Safe fallbacks so the module never renders a blank message if a
    // translation attr went missing (defensive, shouldn't happen in practice).
    messages.required = messages.required || 'This field is required.';
    messages.portRange = messages.portRange || 'Port must be between 1 and 65535.';
    messages.hostname = messages.hostname || 'Enter a valid hostname.';
    messages.email = messages.email || 'Enter a valid email address.';
    messages.fixHighlighted = messages.fixHighlighted || 'Please fix the highlighted fields before running the test.';
    messages.testing = messages.testing || 'Testing…';
    messages.probing = messages.probing || 'Running probe…';
    messages.success = messages.success || 'connection successful.';
    messages.failed = messages.failed || 'failed:';
    messages.networkError = messages.networkError || 'Network error:';

    initTabSwitching(root);
    initOAuthSpinner(root);
    initImapSmtpForm(root, messages, options);
    initCredentialsSpinner(root);
}

/**
 * Auto-init from `data-auto-init="emailSettings"` on the root card.
 */
export function autoInitEmailSettings() {
    var root = document.querySelector('[data-auto-init="emailSettings"]');
    if (!root) return;

    var ds = root.dataset;
    initEmailSettings({
        root: root,
        hasExistingPassword: ds.hasExistingPassword === '1',
        testUrlTemplate: ds.testUrlTemplate || '',
        messages: {
            required: ds.requiredMessage,
            portRange: ds.portRangeMessage,
            hostname: ds.hostnameMessage,
            email: ds.emailMessage,
            fixHighlighted: ds.fixHighlightedMessage,
            testing: ds.testingMessage,
            probing: ds.probingMessage,
            success: ds.successMessage,
            failed: ds.failedMessage,
            networkError: ds.networkErrorMessage,
        },
    });
}
